import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Hammer, ShieldCheck, Droplets, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const FEATURED = [
  {
    num: "01",
    title: "Chimney Sweeping",
    badge: "$249 Special — Free Inspection Included",
    desc: "A full professional sweep plus a free top-to-bottom inspection. We remove creosote, soot, and blockages so your fireplace burns clean and safe all winter.",
    photoTitle: "Chimney Sweeping",
    ctaLabel: "Book the $249 Sweep",
    ctaHref: "#book",
    primary: true,
  },
  {
    num: "02",
    title: "Chimney Crown Rebuild",
    desc: "We grind out cracked, moss-eaten crowns and rebuild them with a fresh mortar wash, new flue tiles, and a cap — so water sheds off instead of soaking into your masonry.",
    photoTitle: "Crown Repair & Rebuild",
    ctaLabel: "Get a Crown Quote",
    ctaHref: "#quote",
  },
  {
    num: "03",
    title: "Tuckpointing & Chase Covers",
    desc: "Precision repointing of failed mortar joints plus new metal chase covers — stopping leaks at the top and restoring the look of your brickwork.",
    photoTitle: "Chase Cover & Tuckpointing",
    ctaLabel: "Get a Tuckpointing Quote",
    ctaHref: "#quote",
  },
  {
    num: "04",
    title: "Fireplace Glass Doors",
    desc: "Custom-fit glass door installation for a cleaner hearth, better draft control, and a safer fireplace your family can gather around.",
    photoTitle: "Fireplace Glass Door Installation",
    ctaLabel: "Get a Glass Door Quote",
    ctaHref: "#quote",
  },
];

const MORE_SERVICES = [
  {
    icon: Hammer,
    title: "Chimney Repair",
    desc: "From cracked flue liners to leaning stacks, we diagnose and repair structural chimney problems that threaten your home's safety.",
  },
  {
    icon: ShieldCheck,
    title: "Chimney Caps",
    desc: "Quality cap installation keeps rain, animals, and debris out of your flue while improving draft and spark protection.",
  },
  {
    icon: Droplets,
    title: "Flashing",
    desc: "We repair and replace the flashing where your chimney meets the roof — the number one source of chimney leaks.",
  },
];

const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export function Services() {
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((res) => setPhotos(res.data))
      .catch(() => setPhotos([]));
  }, []);

  const photoFor = (title) => photos.find((p) => p.title === title);

  return (
    <section id="services" data-testid="services-section" className="py-24 lg:py-32 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="max-w-2xl mb-20"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
            What We Do
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight font-bold text-foreground">
            Chimney & Masonry Specialists
          </h2>
          <p className="text-base text-muted-foreground mt-4 leading-relaxed">
            One call handles everything from routine sweeps to full masonry restoration —
            with real before-and-after proof from jobs across greater Portland.
          </p>
        </motion.div>

        <div className="space-y-24 lg:space-y-32">
          {FEATURED.map((s, i) => {
            const photo = photoFor(s.photoTitle);
            const flip = i % 2 === 1;
            return (
              <motion.div
                key={s.num}
                data-testid={`service-row-${slug(s.title)}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-center"
                initial={{ opacity: 0, y: 48 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: EASE }}
              >
                <div className={`md:col-span-7 ${flip ? "md:order-2" : ""}`}>
                  <div className="flex items-center gap-4 mb-4 flex-wrap">
                    <span className="chapter-number font-heading text-6xl font-extrabold select-none leading-none">
                      {s.num}
                    </span>
                    {s.badge && (
                      <span
                        data-testid="service-sweep-badge"
                        className="bg-primary text-white text-xs uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-md"
                      >
                        {s.badge}
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-foreground mb-4">
                    {s.title}
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed max-w-xl mb-6">
                    {s.desc}
                  </p>
                  {s.primary ? (
                    <Button
                      asChild
                      data-testid="service-book-sweep-button"
                      className="btn-press bg-primary hover:bg-[#A93A24] text-white font-bold rounded-md h-12 px-8"
                    >
                      <a href={s.ctaHref} className="flex items-center gap-2">
                        {s.ctaLabel} <ArrowRight className="w-4 h-4" strokeWidth={2} />
                      </a>
                    </Button>
                  ) : (
                    <a
                      href={s.ctaHref}
                      data-testid={`service-quote-link-${slug(s.title)}`}
                      className="inline-flex items-center gap-2 text-sm font-bold text-primary hover:gap-3 transition-[gap]"
                    >
                      {s.ctaLabel} <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </a>
                  )}
                </div>
                {photo && (
                  <div className={`md:col-span-5 ${flip ? "md:order-1" : ""}`}>
                    <div className="card-lift bg-card border border-border rounded-md overflow-hidden">
                      <img
                        src={`${API}/files/${photo.storage_path}`}
                        alt={`${s.title} before and after`}
                        loading="lazy"
                        data-testid={`service-photo-${slug(s.title)}`}
                        className="w-full h-auto block"
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="mt-24 lg:mt-32">
          <motion.p
            className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-8"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            More Services
          </motion.p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {MORE_SERVICES.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                data-testid={`service-card-${slug(title)}`}
                className="card-lift bg-card border border-border rounded-md p-8 flex flex-col"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: EASE, delay: i * 0.07 }}
              >
                <span className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center mb-6">
                  <Icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </span>
                <h3 className="font-heading text-xl tracking-tight font-bold text-foreground mb-3">
                  {title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{desc}</p>
                <a
                  href="#quote"
                  data-testid={`service-quote-link-${slug(title)}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary hover:gap-3 transition-[gap]"
                >
                  Get a quote <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
